/**
 * Authentication utilities for the HEAT Client Portal
 * Handles JWT token storage and authentication state
 * 
 * Supports two user types:
 * 1. Client Portal Users - standard access to their assigned client
 * 2. Admin Users (Master Key) - access to any client workspace
 */

const TOKEN_KEY = 'heat_portal_token';
const USER_KEY = 'heat_portal_user';
const CLIENTS_KEY = 'heat_portal_clients';
const SELECTED_CLIENT_KEY = 'heat_portal_selected_client';

/**
 * Client information for admin user client selection
 */
export interface PortalClient {
  id: string;
  name: string;
}

/**
 * Portal user - can be either a client user or an admin user
 */
export interface PortalUser {
  id: number | string;
  email: string;
  fullName: string;
  clientId?: string;  // Only present for client users
  isActive?: boolean;
  createdAt?: string;
  client?: {
    id: string;
    name: string;
    industry?: string;
  };
  // Admin user specific fields
  firstName?: string;
  lastName?: string;
  role?: string;
  isAdminPortalUser?: boolean;
}

/**
 * Login API response structure
 */
export interface LoginResponse {
  token: string;
  user: PortalUser;
  clients?: PortalClient[];  // Only present for admin users
}

export const auth = {
  /**
   * Store authentication token and user data
   * For admin users, also stores the available clients list
   */
  setAuth(response: LoginResponse): void {
    localStorage.setItem(TOKEN_KEY, response.token);
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    
    // For admin users, store the clients list in sessionStorage
    if (response.user.isAdminPortalUser && response.clients) {
      sessionStorage.setItem(CLIENTS_KEY, JSON.stringify(response.clients));
    }
  },

  /**
   * Get stored authentication token
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Get stored user data
   */
  getUser(): PortalUser | null {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  /**
   * Check if current user is an admin using master key access
   */
  isAdminPortalUser(): boolean {
    const user = this.getUser();
    return user?.isAdminPortalUser === true;
  },

  /**
   * Get authorization header for API requests
   */
  getAuthHeader(): Record<string, string> {
    const token = this.getToken();
    if (!token) return {};
    return {
      Authorization: `Bearer ${token}`,
    };
  },

  /**
   * Clear authentication data and logout
   */
  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(CLIENTS_KEY);
    sessionStorage.removeItem(SELECTED_CLIENT_KEY);
  },

  /**
   * Get client ID of the authenticated user
   * For admin users, returns the selected client ID from sessionStorage
   * For client users, returns their assigned client ID
   */
  getClientId(): string | null {
    const user = this.getUser();
    if (!user) return null;
    
    if (user.isAdminPortalUser) {
      // Admin user - get selected client from sessionStorage
      return sessionStorage.getItem(SELECTED_CLIENT_KEY);
    }
    
    // Client user - return their assigned client
    return user.clientId || null;
  },

  /**
   * Get the list of available clients (for admin users)
   */
  getAvailableClients(): PortalClient[] {
    const clientsStr = sessionStorage.getItem(CLIENTS_KEY);
    if (!clientsStr) return [];
    try {
      return JSON.parse(clientsStr);
    } catch {
      return [];
    }
  },

  /**
   * Set the selected client for admin users
   */
  setSelectedClient(clientId: string): void {
    sessionStorage.setItem(SELECTED_CLIENT_KEY, clientId);
  },

  /**
   * Get the selected client details (for admin users)
   */
  getSelectedClient(): PortalClient | null {
    const clientId = sessionStorage.getItem(SELECTED_CLIENT_KEY);
    if (!clientId) return null;
    
    const clients = this.getAvailableClients();
    return clients.find(c => c.id === clientId) || null;
  },

  /**
   * Check if admin user has selected a client
   */
  hasSelectedClient(): boolean {
    const user = this.getUser();
    if (!user) return false;
    
    if (user.isAdminPortalUser) {
      return !!sessionStorage.getItem(SELECTED_CLIENT_KEY);
    }
    
    // Client users always have a client
    return !!user.clientId;
  },

  /**
   * Clear the selected client (for admin users to switch clients)
   */
  clearSelectedClient(): void {
    sessionStorage.removeItem(SELECTED_CLIENT_KEY);
  },
};
